import { identifyEnvironment } from "@/utils/IdentifyGlobalEnv";
import type { ObservabilityConfig } from "@/utils/observability";

// Datadog RUM credentials for the feegow-ai-assistant-plugin-v2-frontend RUM
// Application. Both values are public client-side identifiers (designed for
// browser bundle exposure — they are NOT secrets). The same applicationId /
// clientToken are reused across environments because the DD `env` tag set in
// `initObservability` is what segregates local / staging / production
// sessions in RUM Explorer (filter `@env:production`). This matches the
// pattern used by every other Docplanner FE RUM Application — one
// applicationId per service, env tag differentiates: `one-front-app` spans
// `local-development` + `p1-production` + `p2-production` + `p3-production`
// on a single applicationId; same for `sso-app`, `monolith-app`,
// `cliniccloud-front-app`, `one-mobile`.
// `production` is intentionally left empty until staging has been smoke-tested
// end-to-end with the GrowthBook kill switch on. `initObservability`
// short-circuits when applicationId/clientToken are empty, so prod stays
// no-op regardless of GB flag state.
const APPLICATION_ID_BY_ENV: Record<string, string> = {
  local: "d1ba87f7-1561-4508-80b1-d89cdb8f3935",
  staging: "d1ba87f7-1561-4508-80b1-d89cdb8f3935",
  production: "",
};

const CLIENT_TOKEN_BY_ENV: Record<string, string> = {
  local: "pubab186ad7a0215287779e214a5e12ea49",
  staging: "pubab186ad7a0215287779e214a5e12ea49",
  production: "",
};

// Clinical / regulatory PII to strip from log contexts in addition to the
// library defaults (email/password/token/phone/address).
const FEEGOW_FORBIDDEN_KEYS = [
  "cpf",
  "rg",
  "cep",
  "patientName",
  "patient_name",
  "patientId",
  "patient_id",
  "summaryText",
  "summary_text",
  "summary_content",
  "summary",
  "anamnesis",
  "medicalHistory",
  "medical_history",
  "diagnosis",
  "notes",
  "transcription",
  "prescription",
];

// Origins where outgoing fetch/XHR must carry Datadog trace-propagation headers
// for FE↔BE distributed tracing (matches the v2-api APM service).
const ALLOWED_TRACING_URLS = ["https://ai-assistant-v2.feegow.com", "https://ai-assistant-v2.staging.feegow.com"];

// Build-time default sample rate. Kept at 0 so a build that ever runs
// initObservability without the GrowthBook gate (or with the flag flipped on
// before a numeric rate is provisioned) does not accidentally start capturing
// at 100%. The runtime knob is `NOA_browser_rum_sample_rate` in GrowthBook
// (see `initWithGrowthBookGate.ts`), which overrides this on every page load.
const SESSION_SAMPLE_RATE = 0;

export function buildFeegowObservabilityConfig(): ObservabilityConfig {
  const env = identifyEnvironment();
  return {
    applicationId: APPLICATION_ID_BY_ENV[env] ?? "",
    clientToken: CLIENT_TOKEN_BY_ENV[env] ?? "",
    env,
    service: "feegow-ai-assistant-plugin-v2-frontend",
    sessionSampleRate: SESSION_SAMPLE_RATE,
    allowedTracingUrls: ALLOWED_TRACING_URLS,
    additionalForbiddenKeys: FEEGOW_FORBIDDEN_KEYS,
  };
}
